import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import routes from 'sitewide-js/core_pre_webpack/routes';
import { Body } from 'js/design_system/Body';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { imageUrl } from 'js/utils/image_url';
import {
  getCitingSources,
  emptyCitingSources,
  CitingSources,
} from 'js/profiles/api/mentions_api';

// `radius-sm`, not the `radius-lg` the Figma spec asked for: this card sits in the profile
// left rail directly above Suggested Academics, and both that card and the rail wrapper
// itself are 4px (`.suggested-academics-container` / `.user-info-component-wrapper` in
// wow/_profile.scss). A 16px shell read as the odd one out in the column.
const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-xs-12, 12px);

  padding: var(--spacing-xs-16, 16px);
  border: 1px solid var(--stroke-dark, #141413);
  border-radius: var(--border-radius-radius-sm, 4px);
  background: var(--background-beige-med, #f2f0ec);
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-xs-8, 8px);
  align-self: stretch;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-4, 4px);
  word-break: break-word;
`;

// `bold` alone renders at regular weight sitewide: .ds2-5-body-md-bold's weight token is
// undefined and has no fallback. Drop this override when AIC-228 fixes the token.
const Title = styled(Body).attrs({ as: 'span', size: 'md', bold: true })`
  font-weight: var(--type-body-md-bold-font-weight, 600);
`;

const Divider = styled.div`
  height: 1px;
  align-self: stretch;
  background: var(--background-beige-dark, #e2e0d9);
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-xs-8, 8px);
  align-self: stretch;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
  align-self: stretch;
`;

// The site-wide placeholder avatar, for rows whose citation reaches no photo-bearing Academia
// author. Keep it relative: with a leading `/images/`, `imageUrl` passes the path straight
// through and skips the CDN. Payload photo URLs are absolute and pass through untouched.
const PLACEHOLDER_PORTRAIT = 's65_no_pic.png';

// The descriptor stays anonymized and the photo stays decorative (`alt=""`, `aria-hidden`):
// announcing the face would identify the person the copy declines to name.
//
// `blurred` is a typed prop astroturf maps to a `.blurred` class at build time; a
// `${props => ...}` interpolation does not work in an astroturf template.
// The blur is presentation, not an entitlement check.
const AvatarSlot = styled.div<{ blurred?: boolean }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  width: 40px;
  height: 40px;
  border-radius: 100px;
  background: var(--background-beige-dark, #e2e0d9);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  // Oversized by 3 standard deviations (12px) on every side, and only when blurred. A Gaussian
  // samples past the element box, so an img that exactly fills the slot averages its outer ring
  // against transparency: alpha falls to 0.49 at the rim and the face fades into the beige
  // instead of staying opaque. The overscan gives the kernel real pixels; overflow: hidden crops
  // it back to the 40px circle. Do not swap this for transform: scale() -- that scales the blur
  // along with the image and still leaves a translucent rim.
  &.blurred img {
    width: calc(100% + 24px);
    height: calc(100% + 24px);
    flex-shrink: 0;
    filter: blur(4px);
    pointer-events: none;
  }
`;

const Descriptor = styled.div`
  flex: 1 0 0;
  min-width: 0;
  word-break: break-word;
  color: var(--text-primary, #141413);
`;

const Secondary = styled.span`
  color: var(--text-secondary, #535366);
`;

// The `citations-` prefix gates CheckoutController#citations_checkout_entry? — do not rename.
const CTA_TRIGGER = 'citations-promo-profile-rail';

// `top_ids`, not `top_mention_ids` — the latter is the legacy /mentions param, ignored here.
// `_url`, not `_path`: /citations is declared inside `subdomain :www`, but this card renders on
// the profile page, which only routes under ProfileConstraint — i.e. always on a non-www
// subdomain (`independent.` when the owner has no affiliation). A host-relative /citations would
// 404 there. Same reason the navbar and the sibling home card both use citations_url.
const citationHref = (citationId: number) =>
  routes.citations_url({ top_ids: citationId, trigger: CTA_TRIGGER });

// Exported so tests can call it directly: under the astroturf jest mock `blurred` is spread onto
// a plain <div>, and React drops boolean-valued unknown attributes, so the prop leaves no trace
// in the DOM — a markup assertion would pass whatever this returns.
export const shouldBlurPhoto = (
  photosBlurred: boolean,
  photoUrl: string | null,
): boolean => photosBlurred && photoUrl != null;

export const CitingSourcesCard: React.FC<{ userId: number | string }> = ({
  userId,
}) => {
  const [data, setData] = useState<CitingSources | null>(null);

  // One fetch per userId — no polling, no retry. Null until resolved, and empty renders nothing.
  useEffect(() => {
    let cancelled = false;
    getCitingSources(String(userId))
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setData(emptyCitingSources());
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (data == null || data.rows.length === 0) return null;

  const { rows, photosBlurred } = data;

  return (
    <ImpressionWatcher
      source="profile.citing_sources_card"
      extra={{
        rows: rows.length,
        photo_rows: rows.filter((row) => row.photo_url != null).length,
        journal_rows: rows.filter((row) => row.kind === 'journal').length,
      }}
    >
      <Card>
        <Header>
          <TitleRow>
            <Title>Who is citing your work</Title>
            <Body size="xs" as="span" aria-hidden="true">
              |
            </Body>
            <Body size="xs" as="span">
              <Secondary>Private to you</Secondary>
            </Body>
          </TitleRow>
          <Divider />
        </Header>
        <Rows>
          {rows.map((row) => (
            <Row key={row.citation_id}>
              <AvatarSlot
                blurred={shouldBlurPhoto(photosBlurred, row.photo_url)}
              >
                <img
                  src={imageUrl(row.photo_url ?? PLACEHOLDER_PORTRAIT)}
                  alt=""
                  aria-hidden="true"
                  width={40}
                  height={40}
                />
              </AvatarSlot>
              <Descriptor>
                <Body size="sm" bold as="span">
                  {row.descriptor}
                </Body>
              </Descriptor>
              <ButtonLink
                href={citationHref(row.citation_id)}
                size="xs"
                trackClick={{
                  source: 'profile.citing_sources_card.view_click',
                }}
              >
                View
              </ButtonLink>
            </Row>
          ))}
        </Rows>
      </Card>
    </ImpressionWatcher>
  );
};

export default CitingSourcesCard;
